// THE LIMITATIONS PAGE. What this site does not do, and what it does only approximately.
//
// Same pattern as the methodology and Questions and Answers pages: no router, a view swap
// through pushState, its own title, description and canonical link, and the same two-link nav
// back. The content is drawn from ACCURACY_AND_LIMITATIONS.md - the entries a reader of the
// site is owed rather than the whole register, which runs to two hundred thousand characters
// and is written for whoever maintains this.
//
// PLAIN AND FACTUAL. No apology, and no promise that any of it will change.
import React from 'react';
import { C, F, S, W, SP, R } from './tokens';
import { navigate, METHODOLOGY_PATH } from './Explain';
import { QUESTIONS_PATH } from './Questions';
import { useRouteHead } from './head';

export const LIMITATIONS_PATH = '/limitations';

const SECTIONS = [
  {
    heading: 'The data',
    points: [
      'Every figure comes from one source, Yahoo Finance, and none of it is audited by this site. Check anything that matters against the company’s own filings.',
      'The source reports some Indian companies’ statements in dollars while quoting their shares in rupees. Which companies those are is decided from the figures rather than from the currency label, because the label is wrong in both directions. Where it cannot be settled, the company is refused rather than shown.',
      'Gross profit and gross margin are not shown. Indian companies do not report them in a form that can be verified here.',
      'Shareholding percentages are approximate. Check them against the exchange filings before relying on them.',
      'A company’s sector and industry labels come from the data source or the NSE roster. They say nothing about product overlap, customers, or how well two businesses would combine.',
    ],
  },
  {
    heading: 'Valuation',
    points: [
      'Every valuation here is a standardised model run on default assumptions. None of them is a fairness opinion, and none has been reviewed by anyone.',
      'Banks, lenders and insurers are valued on price to book against their own peer group. That needs at least three comparable peers with a meaningful multiple; below that no value is shown.',
      'For insurers, embedded value is the accepted method and this data source does not carry it.',
      'Where a value sits below half or above double the market price, the page says so. The figure is still what the model produces.',
      'Peer multiples are trading multiples for minority stakes in listed companies. They carry no control premium, so they are not precedent transaction multiples.',
    ],
  },
  {
    heading: 'The deal model',
    points: [
      'Synergies are a sector benchmark applied to the target’s revenue. They are not company guidance and not a forecast.',
      'The cash half of the consideration is treated as borrowed at a single rate, and the interest is charged against pro forma earnings. No financing has been arranged or priced.',
      'Accretion and dilution are not meaningful where the acquirer makes a loss, where the arithmetic implies an implausible change, or where the target’s own earnings are erratic. The page says which applies.',
      'Nothing here models integration cost, timing, regulatory conditions, tax structuring or the behaviour of either share price.',
      'The optimizer searches the consideration mix only. It does not search the premium, and its answer is not a recommendation.',
    ],
  },
  {
    heading: 'Regulation and accounting',
    points: [
      'The regulatory flags are a reading of published thresholds against the figures on screen. They are not legal advice and not a filing position.',
      'Competition thresholds are tested against consolidated totals, because the data carries no India-only split.',
      'Whether a company is a public sector enterprise is decided from a list kept with this site, which covers central public sector enterprises, public sector banks and public sector insurers. It does not cover state government enterprises.',
      'Accounting treatment above and below fifty per cent differs, and the model follows the ownership level set on the page. Below twenty per cent nothing is consolidated at all.',
    ],
  },
  {
    heading: 'Freshness',
    points: [
      'The share price and market capitalisation are at most fifteen minutes old. Statements, share-price history and exchange rates can be up to twenty-four hours old.',
      'Where the source returns nothing, the last stored copy may be served instead, up to seven days old. The page says so and gives its date.',
      'Statements are whatever the source last published, so a company that has reported since may not be reflected yet.',
      'The peer roster’s own size figures were built in August 2026 and are not refreshed. They affect which peers are offered, never a figure shown.',
    ],
  },
];

export function LimitationsPage({ productName }) {
  useRouteHead({
    title: `Limitations | ${productName}`,
    description: 'What Dealquire does not do, and what it does only approximately: the data, '
      + 'valuation, the deal model, regulation and freshness.',
    path: LIMITATIONS_PATH,
  });

  return (
    <div style={{ minHeight: '100vh', backgroundColor: C.bg, color: C.text, fontFamily: F.ui,
                  padding: `${SP.xl} ${SP.lg} ${SP.max}` }}>
      <div style={{ maxWidth: '48rem', margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: SP.lg, flexWrap: 'wrap' }}>
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}
             style={{ color: C.accent, fontSize: S.small, fontWeight: W.semi,
                      textDecoration: 'none' }}>
            Back to the deal analysis
          </a>
          <a href={METHODOLOGY_PATH}
             onClick={e => { e.preventDefault(); navigate(METHODOLOGY_PATH); }}
             style={{ color: C.textMuted, fontSize: S.small, fontWeight: W.semi,
                      textDecoration: 'none' }}>
            Methodology
          </a>
          <a href={QUESTIONS_PATH}
             onClick={e => { e.preventDefault(); navigate(QUESTIONS_PATH); }}
             style={{ color: C.textMuted, fontSize: S.small, fontWeight: W.semi,
                      textDecoration: 'none' }}>
            Questions and Answers
          </a>
        </div>

        <h1 style={{ color: C.text, fontFamily: F.display, fontSize: S.h1,
                     marginTop: SP.xl, marginBottom: SP.med }}>
          Limitations
        </h1>
        <p style={{ color: C.textMuted, fontSize: S.body, lineHeight: 1.6,
                    marginBottom: SP.xl }}>
          {productName} runs standardised models on data it does not own. This page says what it
          does not do, and what it does only approximately. Read it before you rely on anything
          here.
        </p>

        {SECTIONS.map(s => (
          <section key={s.heading} id={s.heading.toLowerCase().replace(/[^a-z]+/g, '-')}
                   style={{ marginBottom: SP.xlr }}>
            <h2 style={{ color: C.accent, fontFamily: F.display, fontSize: S.h3,
                         marginBottom: SP.med }}>
              {s.heading}
            </h2>
            <div style={{ backgroundColor: C.surface, borderRadius: R.md, padding: SP.lgr,
                          border: `1px solid ${C.line}` }}>
              {s.points.map((p, i) => (
                <p key={i} style={{ color: C.textMuted, fontSize: S.body, lineHeight: 1.65,
                                    margin: i === 0 ? 0 : `${SP.med} 0 0` }}>
                  {p}
                </p>
              ))}
            </div>
          </section>
        ))}

        <p style={{ color: C.textFaint, fontSize: S.small, lineHeight: 1.6 }}>
          This analysis comes from standardised models and is not investment advice. Check all
          outputs independently before acting on them.
        </p>
      </div>
    </div>
  );
}
